/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGraduationCap, faMapMarkerAlt, faUser, faPhone,
  faEnvelope, faLink, faUpload, faCheckCircle, 
  faInfoCircle, faCheck, faShieldAlt, faSave, faBuilding,
  faUsers, faBriefcase, faGlobe, faFileAlt,
  faChartLine, faCertificate, faCalendar, faTimes, faEdit, faImage,
  faIdCard, faHome, faSchool, faChevronDown, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faFacebook, faTwitter, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { buildApiUrl } from '../config/api';
import { useAutoSave } from '../hooks/useAutoSave';
import axios from 'axios';
import PhoneInput from '../components/PhoneInput';
import '../styles/RecruiterRegistrationForm.css';
import logoImage from '../assets/FINAL LOGO AK.png';

const INSTITUTE_TYPES = [
  "University",
  "College",
  "Technical Institute",
  "Vocational Training Center",
  "Professional Development Center",
  "Online Learning Platform"
];

const COURSES_OFFERED = [
  "Engineering & Technology",
  "Medicine & Healthcare",
  "Business & Management",
  "Arts & Humanities",
  "Science & Research",
  "IT & Computer Science",
  "Law & Legal Studies",
  "Hospitality & Tourism",
  "Education & Teaching",
  "Agriculture & Veterinary",
  "Architecture & Design",
  "Media & Communications",
  "Finance & Accounting",
  "Nursing & Paramedical",
  "Pharmacy & Pharmaceutical",
  "Aviation & Aerospace",
  "Psychology & Social Work",
  "Fashion & Textile",
  "Sports & Physical Education",
  "Environmental Studies",
  "Culinary Arts",
  "Performing Arts",
  "Maritime & Marine Studies",
  "Other"
];

const InstituteRegistrationForm = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isCoursesDropdownOpen, setIsCoursesDropdownOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const coursesDropdownRef = useRef(null);

  // Auto-save configuration
  const AUTOSAVE_KEY = `institute_registration_${user?.userId || 'temp'}`;

  // Use auto-save hook
  const {
    formData,
    setFormData,
    isSaving,
    saveStatus,
    lastSaveTime,
    lastBackendSaveTime,
    forceSave,
    hasRecentSave,
    clearSavedData
  } = useAutoSave({
    // Basic Information
    instituteName: '',
    registrationNumber: '',
    yearEstablished: '',
    instituteType: '',
    instituteLogo: '',
    
    // Contact Information
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    phone: '',
    email: '',
    website: '',
    linkedin: '',
    facebook: '',
    twitter: '',
    instagram: '',
    
    // Primary Contact Person
    contactName: '',
    designation: '',
    contactEmail: '',
    contactPhone: '',
    
    // Institute Details
    totalStudents: '',
    totalFaculty: '',
    accreditation: '',
    accreditationNumber: '',
    campusSize: '',
    departments: '',
    placementRate: '',
    avgPackage: '',
    about: '',
    
    // Agreements
    agreeTerms: false
  },
  AUTOSAVE_KEY,
  1000,
  null,
  true
  );

  const [courses, setCourses] = useState([]);

  // Persist array-based fields across refreshes
  const ARRAYS_AUTOSAVE_KEY = useMemo(() => `${AUTOSAVE_KEY}_arrays`, [AUTOSAVE_KEY]);

  // Hydrate arrays on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(ARRAYS_AUTOSAVE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed?.courses)) setCourses(parsed.courses);
      }
    } catch (_) { /* ignore */ }
  }, [ARRAYS_AUTOSAVE_KEY]);

  // Save arrays whenever they change
  useEffect(() => {
    const payload = { courses };
    try {
      localStorage.setItem(ARRAYS_AUTOSAVE_KEY, JSON.stringify(payload));
    } catch (_) { /* ignore quota errors */ }
  }, [courses, ARRAYS_AUTOSAVE_KEY]);

  // Update logo preview when formData.instituteLogo changes
  useEffect(() => {
    if (formData.instituteLogo && formData.instituteLogo !== logoPreview) {
      setLogoPreview(formData.instituteLogo);
    }
  }, [formData.instituteLogo]);

  // Close courses dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (coursesDropdownRef.current && !coursesDropdownRef.current.contains(event.target)) {
        setIsCoursesDropdownOpen(false);
        setCourseSearchQuery('');
      }
    };

    if (isCoursesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCoursesDropdownOpen]);

  // Filter courses based on search query
  const filteredCourses = useMemo(() => {
    if (!courseSearchQuery.trim()) {
      return COURSES_OFFERED;
    }
    return COURSES_OFFERED.filter(course =>
      course.toLowerCase().includes(courseSearchQuery.toLowerCase())
    );
  }, [courseSearchQuery]);

  const handleCourseToggle = (course) => {
    setCourses(prev => {
      const updated = prev.includes(course)
        ? prev.filter(c => c !== course)
        : [...prev, course];
      updateProgress();
      return updated;
    });
  };

  const updateProgress = useCallback(() => {
    const requiredFields = [
      formData.instituteName, formData.registrationNumber, formData.yearEstablished,
      formData.instituteType, formData.address, formData.city, formData.state,
      formData.country, formData.phone, formData.email, formData.website,
      formData.contactName, formData.designation, formData.contactEmail,
      formData.contactPhone, formData.totalStudents, formData.totalFaculty,
      formData.accreditation, formData.accreditationNumber, formData.departments,
      formData.placementRate, formData.avgPackage, formData.about
    ];

    const filledFields = requiredFields.filter(field => {
      return field && field.toString().trim() !== '';
    }).length;

    // Count array fields
    let arrayFieldsCount = 0;
    if (courses.length > 0) arrayFieldsCount++;

    // Count terms checkboxes
    let termsCount = 0;
    if (formData.agreeTerms) termsCount++;

    // Total: 23 required fields + 1 array + 1 terms = 25 total
    const totalRequired = 25;
    const totalFilled = filledFields + arrayFieldsCount + termsCount;

    const progressPercent = Math.min((totalFilled / totalRequired) * 100, 100);
    setProgress(progressPercent);
  }, [formData, courses]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    updateProgress();
  }, [updateProgress, setFormData]);


  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      if (!file.type.match(/^image\/(png|jpg|jpeg|svg)$/i)) {
        alert('Only PNG, JPG, and SVG images are allowed');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const logoData = e.target.result;
        setLogoPreview(logoData);
        setFormData(prev => ({
          ...prev,
          instituteLogo: logoData
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Save to backend function
  const saveToBackend = async (showSuccessMessage = false, redirectAfterSave = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️  No token found, skipping backend save');
        return false;
      }

      const submitData = {
        ...formData,
        courses,
        profileCompleted: false,
        isDraft: true,
        draftSavedAt: new Date().toISOString()
      };

      const response = await fetch(buildApiUrl('/api/institute/complete-registration'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        if (showSuccessMessage) {
          setSubmitError('✓ Form saved successfully!');
          setTimeout(() => setSubmitError(''), 3000);
        }
        if (redirectAfterSave) {
          setTimeout(() => {
            navigate('/institute-dashboard');
          }, 1000);
        }
        return true;
      } else {
        const errorData = await response.json();
        if (showSuccessMessage) {
          setSubmitError(errorData.message || 'Failed to save. Please try again.');
        }
        return false;
      }
    } catch (error) {
      console.error('Backend save error:', error);
      if (showSuccessMessage) {
        setSubmitError('Network error. Your data is saved locally.');
      }
      return false;
    }
  };

  // Manual save handler
  const handleManualSave = async () => {
    setIsLoading(true);
    setSubmitError('');
    try {
      forceSave();
      await saveToBackend(true, false);
    } finally {
      setIsLoading(false);
    }
  };

  // Save as draft and redirect
  const handleSaveAsDraft = async () => {
    setIsLoading(true);
    setSubmitError('');
    try {
      forceSave();
      await saveToBackend(true, true);
    } catch (error) {
      console.error('Save draft error:', error);
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError('');

    // Validation
    if (!formData.agreeTerms) {
      setSubmitError('Please accept the terms and conditions');
      setIsLoading(false);
      return;
    }

    if (courses.length === 0) {
      setSubmitError('Please select at least one course/program offered');
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (typeof formData[key] === 'boolean') {
          submitData.append(key, formData[key].toString());
        } else if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      // Add courses
      submitData.append('courses', JSON.stringify(courses));

      // Add logo file if uploaded
      if (logoFile) {
        submitData.append('instituteLogo', logoFile);
      }

      const response = await axios.post(
        buildApiUrl('/api/institute/complete-registration'),
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Clear saved form data on successful submission
        clearSavedData();
        
        // Update localStorage with profile completion status
        localStorage.setItem('profileCompleted', 'true');
        localStorage.setItem('hasCompletedProfile', 'true');
        
        // Update user context to mark profile as completed
        if (typeof updateUser === 'function') {
          updateUser({ 
            profileCompleted: true,
            hasCompletedProfile: true
          });
        }
        
        // Show success message
        alert('✅ Institute registration completed successfully! Redirecting to your dashboard...');
        
        // Force refresh of user data to ensure profile completion is detected
        setTimeout(() => {
          sessionStorage.clear();
          localStorage.setItem('profileCompleted', 'true');
          localStorage.setItem('hasCompletedProfile', 'true');
          window.location.href = '/institute-dashboard';
        }, 1000);
      } else {
        setSubmitError(response.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(error.response?.data?.message || 'Failed to complete registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="recruiter-details-comprehensive">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .recruiter-details-comprehensive {
          max-width: 100% !important;
        }
        .recruiter-header {
          width: 100% !important;
          max-width: 100% !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        .recruiter-header .header-container {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          padding: 12px 20px 12px 10px !important;
          max-width: 100% !important;
          margin: 0 !important;
          width: 100% !important;
        }
        .recruiter-header .header-left {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 12px !important;
          margin-right: auto !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
          flex: 0 0 auto !important;
          order: 1 !important;
        }
        .recruiter-header .header-left .logo-icon {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-left .logo-text {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-right {
          margin-left: auto !important;
          flex: 0 0 auto !important;
          order: 2 !important;
        }
      `}</style>
      
      {/* Header */}
      <header className="recruiter-header">
        <div className="header-container">
          <div className="header-left" onClick={() => navigate('/institute-dashboard')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="logo-icon">
              <img src={logoImage} alt="AksharJobs Logo" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            </div>
            <div className="logo-text">
              <h3 className="logo-title" style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>AksharJobs</h3>
              <p className="logo-subtitle" style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>Institute Registration</p>
            </div>
          </div>
          <div className="header-right">
            <div className="header-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div className="auto-save-status" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                {isSaving && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#f39c12' }}>
                    <div className="spinner" style={{ width: '12px', height: '12px', border: '2px solid #f39c12', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <span>Auto-saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#27ae60' }}>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Auto-saved {lastSaveTime && `at ${lastSaveTime.toLocaleTimeString()}`}</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#e74c3c' }}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Save failed - data preserved locally</span>
                  </div>
                )}
                {saveStatus === 'loaded' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#3498db' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Previous data restored</span>
                  </div>
                )}
                {!isSaving && !saveStatus && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6c757d' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Auto-save enabled</span>
                  </div>
                )}
              </div>
              <button
                className="manual-save-btn"
                type="button"
                onClick={handleManualSave}
                disabled={isSaving || isLoading}
                style={{
                  background: (isSaving || isLoading) ? '#bdc3c7' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: (isSaving || isLoading) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faSave} /> {(isSaving || isLoading) ? 'Saving...' : 'Save Now'}
              </button>
              <button
                className="draft-save-btn"
                type="button"
                onClick={handleSaveAsDraft}
                disabled={isLoading}
                style={{
                  background: isLoading ? '#bdc3c7' : '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                <FontAwesomeIcon icon={faEdit} /> Save as Draft
              </button>
              <button
                className="fill-test-data-btn"
                type="button"
                onClick={() => {
                  if (window.confirm('This will fill all form fields with test data. Continue?')) {
                    // Fill test data
                    setFormData(prev => ({
                      ...prev,
                      instituteName: 'Technical Training Institute',
                      registrationNumber: 'INST/2024/001',
                      yearEstablished: '2010',
                      instituteType: 'Technical Institute',
                      address: '123 Training Street, Industrial Area',
                      city: 'Nairobi',
                      state: 'Nairobi',
                      country: 'Kenya',
                      postalCode: '00100',
                      phone: '+254 712 345 678',
                      email: 'info@institute.ac.ke',
                      website: 'https://www.institute.ac.ke',
                      linkedin: 'https://linkedin.com/company/institute',
                      facebook: 'https://facebook.com/institute',
                      twitter: '@institute',
                      instagram: '@institute',
                      contactName: 'Dr. Jane Wanjiku',
                      designation: 'Director',
                      contactEmail: 'director@institute.ac.ke',
                      contactPhone: '+254 712 345 679',
                      totalStudents: '2000',
                      totalFaculty: '150',
                      accreditation: 'Technical and Vocational Education and Training Authority',
                      accreditationNumber: 'TVETA/2024/001',
                      campusSize: '5.5',
                      departments: '12',
                      placementRate: '85',
                      avgPackage: 'KES 400,000 per annum',
                      about: 'A leading technical training institute committed to excellence in vocational education and training. We provide hands-on training in various technical fields and have a strong track record of placing our graduates in top companies.'
                    }));
                    
                    // Fill courses
                    setCourses([
                      'Engineering & Technology',
                      'IT & Computer Science',
                      'Business & Management',
                      'Hospitality & Tourism',
                      'Education & Teaching'
                    ]);
                    
                    alert('Test data filled successfully!');
                  }
                }}
                style={{
                  background: '#f39c12',
                  border: 'none',
                  color: 'white',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                Fill Test Data
              </button>
              <button
                className="clear-form-btn"
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all saved form data? This cannot be undone.')) {
                    clearSavedData();
                    window.location.reload();
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #e74c3c',
                  color: '#e74c3c',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease'
                }}
              >
                Clear Form Data
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="recruiter-main">
        <div className="recruiter-container">
          <div className="recruiter-form-card">
            {/* Main Form */}
            <div className="recruiter-form-container">
              <form id="instituteForm" onSubmit={handleSubmit}>
                {/* Enhanced Progress Section */}
                <div className="progress-section-comprehensive" style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '30px',
                  color: '#000000',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '2px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="progress-header-comprehensive" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#000000' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '10px', color: '#0d9488' }} />
                      Registration Progress
                    </h2>
                    <div style={{
                      background: '#f5f5f5',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#000000'
                    }}>
                      {Math.round(progress)}%
                    </div>
                  </div>
                  
                  <div className="progress-bar-comprehensive" style={{
                    background: '#f5f5f5',
                    height: '12px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    marginBottom: '15px',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}>
                    <div 
                      className="progress-fill-comprehensive" 
                      style={{ 
                        width: `${progress}%`,
                        height: '100%',
                        background: '#0d9488',
                        borderRadius: '6px',
                        transition: 'width 0.5s ease-in-out',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        height: '100%',
                        width: '20px',
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 100%)',
                        borderRadius: '0 6px 6px 0'
                      }}></div>
                    </div>
                  </div>

                  {/* Progress Status Message */}
                  <div style={{
                    fontSize: '14px',
                    opacity: 0.9,
                    textAlign: 'center',
                    marginBottom: '10px',
                    color: '#000000'
                  }}>
                    {Math.round(progress) === 100 ? (
                      <span style={{ color: '#0d9488', fontWeight: '600' }}>
                        🎉 Profile Complete! Ready to submit.
                      </span>
                    ) : Math.round(progress) >= 80 ? (
                      <span style={{ color: '#000000' }}>
                        Almost there! Just a few more fields to complete.
                      </span>
                    ) : Math.round(progress) >= 50 ? (
                      <span style={{ color: '#000000' }}>
                        Good progress! Keep filling out the form.
                      </span>
                    ) : (
                      <span style={{ color: '#000000' }}>
                        Let's get started! Fill out your institute information.
                      </span>
                    )}
                  </div>

                  {/* Quick Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                    gap: '10px',
                    fontSize: '12px',
                    marginBottom: '15px',
                    color: '#000000'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>6</div>
                      <div style={{ color: '#000000' }}>Sections</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>25+</div>
                      <div style={{ color: '#000000' }}>Fields</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#000000' }}>Auto-save</div>
                      <div style={{ color: '#000000' }}>Enabled</div>
                    </div>
                  </div>

                  {/* Section Completion Checklist */}
                  {Math.round(progress) < 100 && (
                    <div style={{
                      background: '#f5f5f5',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#000000' }}>
                        Complete these sections:
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: '6px'
                      }}>
                        {[
                          { key: 'basic', name: 'Basic Info', icon: '🏛️', completed: formData.instituteName && formData.instituteType && formData.registrationNumber },
                          { key: 'contact', name: 'Contact Info', icon: '📧', completed: formData.email && formData.phone && formData.address },
                          { key: 'person', name: 'Contact Person', icon: '👤', completed: formData.contactName && formData.designation && formData.contactEmail },
                          { key: 'details', name: 'Institute Details', icon: '📄', completed: formData.totalStudents && formData.totalFaculty && formData.accreditation },
                          { key: 'courses', name: 'Courses', icon: '🎓', completed: courses.length > 0 },
                          { key: 'terms', name: 'Terms', icon: '✅', completed: formData.agreeTerms }
                        ].map(section => (
                          <div key={section.key} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: section.completed ? 0.6 : 1
                          }}>
                            <span style={{
                              color: section.completed ? '#0d9488' : '#000000',
                              fontSize: '14px'
                            }}>
                              {section.completed ? '✅' : section.icon}
                            </span>
                            <span style={{
                              textDecoration: section.completed ? 'line-through' : 'none',
                              opacity: section.completed ? 0.7 : 1,
                              color: '#000000'
                            }}>
                              {section.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {submitError && (
                  <div className="error-message" style={{ marginBottom: '20px', padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '6px', color: '#c33' }}>
                    <FontAwesomeIcon icon={faInfoCircle} /> {submitError}
                  </div>
                )}

                {/* Important Instructions Box */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  marginBottom: '30px',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px'
                  }}>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <FontAwesomeIcon 
                        icon={faInfoCircle} 
                        style={{ 
                          fontSize: '24px', 
                          color: '#ffffff' 
                        }} 
                      />
                    </div>
                    <div style={{ flex: 1, color: '#ffffff' }}>
                      <h3 style={{
                        margin: '0 0 12px 0',
                        fontSize: '18px',
                        fontWeight: '700',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>📄 Important Information</span>
                      </h3>
                      <div style={{
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: '#ffffff'
                      }}>
                        <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>
                          <strong>🚫 No CV Upload Allowed:</strong> We do not allow CV/resume uploads. Your profile in this registration form is your CV. Hence, it is essential to complete this registration form thoroughly.
                        </p>
                        <p style={{ margin: '0 0 10px 0', fontWeight: '500' }}>
                          <strong>📄 Your Profile is Your CV:</strong> The information you provide in this registration form will be used to generate your professional CV/resume. Make sure to fill all details correctly and provide accurate information.
                        </p>
                        <p style={{ margin: '0', fontWeight: '500' }}>
                          <strong>💡 Complete 100% for Better Opportunities:</strong> Fill out all sections completely to receive better trainee-internship matches and increase your chances of connecting your trainees with the perfect internship opportunities.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Institution Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faSchool} />
                    Institution Information
                  </h2>

                  {logoPreview && (
                    <div className="profile-photo-preview-comprehensive" style={{ display: 'block' }}>
                      <img src={logoPreview} alt="Institute Logo" />
                    </div>
                  )}

                  <div className="form-group-comprehensive">
                    <label className="file-upload-comprehensive">
                      <FontAwesomeIcon icon={faImage} />
                      <div className="file-upload-text-comprehensive">
                        <strong>Upload Institute Logo</strong>
                        <p>Click to browse or drag and drop (PNG, JPG, SVG, max 2MB)</p>
                      </div>
                      <input type="file" accept="image/*" onChange={handleLogoChange} />
                    </label>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Institute Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="instituteName"
                      value={formData.instituteName}
                      onChange={handleInputChange}
                      placeholder="Enter institute name"
                      required
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Institute Type <span className="required">*</span></label>
                      <select
                        name="instituteType"
                        value={formData.instituteType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Type</option>
                        {INSTITUTE_TYPES.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Year Established <span className="required">*</span></label>
                      <input
                        type="number"
                        name="yearEstablished"
                        value={formData.yearEstablished}
                        onChange={handleInputChange}
                        placeholder="YYYY"
                        required
                        min="1900"
                        max={new Date().getFullYear()}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Registration Number <span className="required">*</span></label>
                    <input
                      type="text"
                      name="registrationNumber"
                      value={formData.registrationNumber}
                      onChange={handleInputChange}
                      placeholder="e.g., INST/2024/001"
                      required
                    />
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faEnvelope} />
                    Contact Information
                  </h2>

                  <div className="form-group-comprehensive">
                    <label>Address <span className="required">*</span></label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      placeholder="Complete address with street, area"
                      rows="3"
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>City <span className="required">*</span></label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        placeholder="City name"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>State/County <span className="required">*</span></label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        placeholder="State/County"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="Postal/ZIP code"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Country <span className="required">*</span></label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      placeholder="Country"
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Phone Number <span className="required">*</span></label>
                      <PhoneInput
                        value={formData.phone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, phone: value }));
                          updateProgress();
                        }}
                        required
                        placeholder="+254 XXX XXX XXX"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Email Address <span className="required">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="contact@institute.com"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Website <span className="required">*</span></label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      required
                      placeholder="https://www.yourwebsite.com"
                    />
                  </div>
                </div>

                {/* Primary Contact Person Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faUser} />
                    Primary Contact Person
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Full Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleInputChange}
                        required
                        placeholder="Full name"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Designation <span className="required">*</span></label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Director, Principal"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Contact Email <span className="required">*</span></label>
                      <input
                        type="email"
                        name="contactEmail"
                        value={formData.contactEmail}
                        onChange={handleInputChange}
                        required
                        placeholder="person@institute.com"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Contact Phone <span className="required">*</span></label>
                      <PhoneInput
                        value={formData.contactPhone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, contactPhone: value }));
                          updateProgress();
                        }}
                        required
                        placeholder="+254 XXX XXX XXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Institute Details Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faFileAlt} />
                    Institute Details
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Total Students <span className="required">*</span></label>
                      <input
                        type="number"
                        name="totalStudents"
                        value={formData.totalStudents}
                        onChange={handleInputChange}
                        required
                        min="1"
                        placeholder="Approximate number"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Total Faculty <span className="required">*</span></label>
                      <input
                        type="number"
                        name="totalFaculty"
                        value={formData.totalFaculty}
                        onChange={handleInputChange}
                        required
                        min="1"
                        placeholder="Number of faculty members"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Number of Departments <span className="required">*</span></label>
                      <input
                        type="number"
                        name="departments"
                        value={formData.departments}
                        onChange={handleInputChange}
                        required
                        min="1"
                        placeholder="Total departments"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Accreditation Body <span className="required">*</span></label>
                      <input
                        type="text"
                        name="accreditation"
                        value={formData.accreditation}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Commission for University Education"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Accreditation Number <span className="required">*</span></label>
                      <input
                        type="text"
                        name="accreditationNumber"
                        value={formData.accreditationNumber}
                        onChange={handleInputChange}
                        required
                        placeholder="Accreditation reference number"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Campus Size (acres)</label>
                      <input
                        type="number"
                        name="campusSize"
                        value={formData.campusSize}
                        onChange={handleInputChange}
                        step="0.01"
                        placeholder="Total area in acres"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Placement Rate (%) <span className="required">*</span></label>
                      <input
                        type="number"
                        name="placementRate"
                        value={formData.placementRate}
                        onChange={handleInputChange}
                        required
                        min="0"
                        max="100"
                        placeholder="e.g., 85"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Average Package Offered <span className="required">*</span></label>
                      <input
                        type="text"
                        name="avgPackage"
                        value={formData.avgPackage}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., KES 500,000 per annum"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Courses/Programs Offered <span className="required">*</span></label>
                    
                    {/* Compact Dropdown Input */}
                    <div ref={coursesDropdownRef} style={{ position: 'relative' }}>
                      <div
                        onClick={() => setIsCoursesDropdownOpen(!isCoursesDropdownOpen)}
                        style={{
                          width: '100%',
                          minHeight: '40px',
                          padding: '8px 12px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          backgroundColor: '#ffffff',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          ...(isCoursesDropdownOpen && {
                            borderColor: '#667eea',
                            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
                          })
                        }}
                      >
                        <span style={{
                          color: courses.length === 0 ? '#999' : '#333',
                          flex: 1
                        }}>
                          {courses.length === 0 
                            ? 'Select courses/programs...' 
                            : `${courses.length} course${courses.length !== 1 ? 's' : ''} selected`}
                        </span>
                        <FontAwesomeIcon 
                          icon={faChevronDown} 
                          style={{
                            fontSize: '12px',
                            color: '#666',
                            transition: 'transform 0.3s ease',
                            transform: isCoursesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                      </div>

                      {/* Dropdown Menu */}
                      {isCoursesDropdownOpen && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '4px',
                          background: '#ffffff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          zIndex: 1000,
                          maxHeight: '300px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          {/* Search Input */}
                          <div style={{
                            padding: '8px',
                            borderBottom: '1px solid #e2e8f0',
                            background: '#f8f9fa'
                          }}>
                            <div style={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <FontAwesomeIcon 
                                icon={faSearch} 
                                style={{
                                  position: 'absolute',
                                  left: '12px',
                                  color: '#999',
                                  fontSize: '14px'
                                }}
                              />
                              <input
                                type="text"
                                placeholder="Search courses..."
                                value={courseSearchQuery}
                                onChange={(e) => setCourseSearchQuery(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px 8px 36px',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  outline: 'none'
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#667eea';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#e2e8f0';
                                }}
                              />
                            </div>
                          </div>

                          {/* Options List */}
                          <div style={{
                            maxHeight: '240px',
                            overflowY: 'auto',
                            padding: '4px 0'
                          }}>
                            {filteredCourses.length === 0 ? (
                              <div style={{
                                padding: '12px',
                                textAlign: 'center',
                                color: '#999',
                                fontSize: '14px'
                              }}>
                                No courses found
                              </div>
                            ) : (
                              filteredCourses.map(course => {
                                const isSelected = courses.includes(course);
                                return (
                                  <div
                                    key={course}
                                    onClick={() => handleCourseToggle(course)}
                                    style={{
                                      padding: '10px 12px',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                      transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isSelected) {
                                        e.target.style.backgroundColor = '#f5f5f5';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!isSelected) {
                                        e.target.style.backgroundColor = 'transparent';
                                      }
                                    }}
                                  >
                                    <div style={{
                                      width: '18px',
                                      height: '18px',
                                      border: `2px solid ${isSelected ? '#1976d2' : '#ccc'}`,
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: isSelected ? '#1976d2' : 'transparent',
                                      flexShrink: 0
                                    }}>
                                      {isSelected && (
                                        <FontAwesomeIcon 
                                          icon={faCheck} 
                                          style={{
                                            color: '#ffffff',
                                            fontSize: '10px'
                                          }}
                                        />
                                      )}
                                    </div>
                                    <span style={{
                                      color: isSelected ? '#1976d2' : '#333',
                                      fontWeight: isSelected ? '500' : '400'
                                    }}>
                                      {course}
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Selected Courses Chips - Display Below Dropdown */}
                    {courses.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        marginTop: '10px',
                        minHeight: '32px'
                      }}>
                        {courses.map(course => (
                          <span
                            key={course}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 10px',
                              background: '#e3f2fd',
                              color: '#1976d2',
                              borderRadius: '16px',
                              fontSize: '12px',
                              fontWeight: '500',
                              border: '1px solid #bbdefb'
                            }}
                          >
                            {course}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCourseToggle(course);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: '0',
                                marginLeft: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                lineHeight: 1,
                                width: '16px',
                                height: '16px'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.color = '#1565c0';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.color = '#1976d2';
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-group-comprehensive">
                    <label>About the Institute <span className="required">*</span></label>
                    <textarea
                      name="about"
                      value={formData.about}
                      onChange={handleInputChange}
                      required
                      placeholder="Brief description of your institute, achievements, and unique features (max 500 words)"
                      rows="4"
                      maxLength="3000"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>{formData.about.length}/3000 characters</small>
                  </div>
                </div>

                {/* Social Media & Optional Info Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faGlobe} />
                    Social Media & Additional Information
                  </h2>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faLinkedin} style={{ color: '#0077B5', marginRight: '8px' }} />
                        LinkedIn Profile
                      </label>
                      <input
                        type="url"
                        name="linkedin"
                        value={formData.linkedin}
                        onChange={handleInputChange}
                        placeholder="https://linkedin.com/company/your-institute"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faFacebook} style={{ color: '#1877F2', marginRight: '8px' }} />
                        Facebook Page
                      </label>
                      <input
                        type="url"
                        name="facebook"
                        value={formData.facebook}
                        onChange={handleInputChange}
                        placeholder="https://facebook.com/your-institute"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faTwitter} style={{ color: '#1DA1F2', marginRight: '8px' }} />
                        Twitter/X Handle
                      </label>
                      <input
                        type="text"
                        name="twitter"
                        value={formData.twitter}
                        onChange={handleInputChange}
                        placeholder="@institutename"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>
                        <FontAwesomeIcon icon={faInstagram} style={{ color: '#E4405F', marginRight: '8px' }} />
                        Instagram Handle
                      </label>
                      <input
                        type="text"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleInputChange}
                        placeholder="@institutename"
                      />
                    </div>
                  </div>
                </div>

                {/* Terms & Agreements Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Terms & Agreements
                  </h2>

                  <div className="checkbox-group" style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    marginBottom: '15px'
                  }}>
                    <input 
                      type="checkbox" 
                      id="agreeTerms" 
                      name="agreeTerms" 
                      required
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      style={{ 
                        marginTop: '3px',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <label htmlFor="agreeTerms" style={{ 
                      textAlign: 'left',
                      margin: 0,
                      cursor: 'pointer',
                      flex: 1
                    }}>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. I confirm that all information provided is accurate and I have the authority to register this institute. <span className="required">*</span></label>
                  </div>

                  {/* Admin Approval Message */}
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      style={{
                        color: '#1976d2',
                        fontSize: '18px',
                        marginTop: '2px',
                        flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1976d2',
                        marginBottom: '6px'
                      }}>
                        Account Approval Process
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#424242',
                        lineHeight: '1.6'
                      }}>
                        Complete your profile <strong>80%</strong>. If it is <strong>80% completed</strong>, it will be reviewed and then approved by our admin team within <strong>24 hours</strong>. You will receive an email notification once your account is approved. Please check your <strong>spam folder</strong> if you don't see the email in your inbox.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Section */}
                <div className="submit-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {submitError && (
                    <div className="error-message" style={{ marginBottom: '12px', color: '#e74c3c', textAlign: 'center' }}>
                      {submitError}
                    </div>
                  )}
                  <button type="submit" className="submit-btn" disabled={isLoading} style={{ margin: '0 auto' }}>
                    <FontAwesomeIcon icon={faCheckCircle} /> 
                    {isLoading ? 'Submitting...' : 'Complete Institute Registration'}
                  </button>
                  <p style={{ marginTop: '8px', color: '#666', fontSize: '12px', textAlign: 'center' }}>
                    Your account will be reviewed and activated within 24-48 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InstituteRegistrationForm;

